import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {Targeting} from "../../../../classes/targeting/targeting";
import {ApiCompanyService} from "../../../../services/sp-api/sp-api-company/api-company.service";
import {Subscription} from "../../../../models/subscription";
import {Project} from "../../../../models/project";
import {ApiProjectService} from "../../../../services/sp-api/sp-api-project/api-project.service";
import {Router} from "@angular/router";
import {ChartService} from '../../../../services/sp-chart/chart.service';
import {AlertService} from '../../../../services/sp-alert/alert.service';
import {LoggerService} from '../../../../services/sp-logger/logger.service';
import {ProjectManagerService} from '../../../../services/sp-project/project-manager.service';
import {UntypedFormControl, Validators} from '@angular/forms';
import {ProjectCreationManagerService} from '../../../../services/sp-project/project-creation-manager.service';
import {TrackerService} from '../../../../services/sp-tracker/tracker.service';
import { SessionProviderService } from 'src/app/services/sp-authentication/session-provider.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-sp-modal-project-create-details',
  templateUrl: './sp-modal-project-create-details.component.html',
  styleUrls: ['./sp-modal-project-create-details.component.scss']
})
export class SpModalProjectCreateDetailsComponent implements OnInit {
  audienceTargetSpec: Targeting = new Targeting();
  audienceBenchmarkSpec: Targeting = new Targeting();

  companySub: Subscription;
  sourceType: string = "default";
  personaMergeMode: string = "include";
  actionPending: boolean = false;
  newCrawlAvailable: boolean = false;

  project: Project;

  // Freemium functionality
  projectNameControl: UntypedFormControl = new UntypedFormControl('', Validators.required);

  // Stated once, and subtracted from the same place it is stated, so the announced cost
  // and the figure the customer is shown can never disagree.
  readonly creditsPerAnalysis = 1;

  // One balance, and the balance after this analysis. Kept out of the template so the
  // spend-order rule has no second definition there: which internal balance a credit
  // comes off is not something a customer should be shown. Both branches of this modal
  // show the AFTER figure — the same sentence must not mean the pre-spend balance on one
  // path and the post-spend balance on the other.
  get creditBalance(): number {
    return this.companySub ? Subscription.totalCredits(this.companySub) : 0;
  }

  get creditBalanceAfter(): number {
    return Math.max(this.creditBalance - this.creditsPerAnalysis, 0);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: {project: Project, freemium: boolean},
    private dialogRef: MatDialogRef<SpModalProjectCreateDetailsComponent>,
    private apiCompany: ApiCompanyService,
    private apiProject: ApiProjectService,
    private creationManager: ProjectCreationManagerService,
    private sessionProvider: SessionProviderService,
    private router: Router,
    private alert: AlertService,
    private logger: LoggerService,
    private projectManager: ProjectManagerService,
    private featureFlagService: FeatureFlagsService,
    private tracker: TrackerService) {
    this.project = this.dialogData.project;
    this.audienceTargetSpec = Targeting.fromAudience(dialogData.project.audience_target);
    this.audienceBenchmarkSpec = Targeting.fromAudience(dialogData.project.audience_bench);
  }

  async ngOnInit() {
    this.apiCompany.getOne(this.sessionProvider.activeSession.user.company.id).then(company => {
      this.companySub = company.subscription;
    });
    this.newCrawlAvailable = await this.featureFlagService.isEnabled('new_crawl');
  }
  onNoClick(): void {
    this.dialogRef.close(null);
  }
  selectedUniversesToString() {
    return this.project.audience_target.universes.map(u => u.name).join(", ");
  }

  get showLegacyCrawlOption(): boolean {
    return this.newCrawlAvailable && environment.config.enableDebugFeatures;
  }

  createProject(useNewCrawl: boolean = false): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.actionPending) {
        this.actionPending = true;
        try {
          if (this.project) {
            this.project.newCrawl = useNewCrawl;

            this.apiProject.postOne(this.project).then((project: Project) => {
              this.projectManager.projectCreatedCount++;

              Subscription.substractCredit(this.sessionProvider.activeSession.user.company.subscription);
              this.tracker.trackCreditsUsed(1, 'report_launched', this.sessionProvider.activeSession.user.company.subscription);

              this.dialogRef.close(project);
              let audienceTargetCriteriaCount = 0;
              const targeting = Targeting.fromJson(project.audience_target.target_spec, project.audience_target.type);
              targeting.flex?.forEach(flex => {
                flex.forEach(arr => {
                  audienceTargetCriteriaCount += arr.length;
                })
              });

              let projectCreationData: any = {
                project: project.name,
                report_id: project.id,
                report_name: project.name,
                targeting: project.audience_target.target_spec,
                type: project.audience_target.type,
                criteria_count: audienceTargetCriteriaCount,
                fb_score: project.audience_target.fb_score_v2,
                origin: 'dashboard'
              }

              this.populateAudienceDefinitionProperties(projectCreationData);

              this.tracker.track('report_created', projectCreationData);
              this.router.navigate(['/projects']).then(() => {
                resolve(project);
              });
            }, error => {
              this.logger.logError("Could not create project : " + error.message, 2);
              this.handleTargetSpecError(error);
              this.dialogRef.close();
              reject(error);
            }).finally(() => this.actionPending = false);
          } else {
            resolve(null);
          }
        } catch (err) {
          this.actionPending = false;
          this.logger.logError('Unexpected error while creating project, this has been reported. You can empty your browser cache and try again.', 3, err);
          reject(err);
        }
      } else {
        resolve(null);
      }
    });
  }

  private populateAudienceDefinitionProperties(projectCreationData: any) {
    if (this.creationManager.selectedAudienceId) {
      projectCreationData.audienceDefinitionId = this.creationManager.selectedAudienceId;
      projectCreationData.audienceDefinitionName = this.creationManager.selectedAudienceName;
      projectCreationData.audienceDefinitionRootCategory = this.creationManager.selectedAudienceRootCategory;

      if (this.creationManager.selectedAudienceParentCategory) {
        projectCreationData.audienceDefinitionParentCategory = this.creationManager.selectedAudienceParentCategory;
      }
    }
  }

  private handleTargetSpecError(error: any): void {
    const body = error.error;
    switch (body?.code) {
      case 'CAPI_META_AUDIENCE_TOO_SMALL':
        this.alert.notify('Audience too small', body.message, 'error', {disableTimeOut: true, tapToDismiss: true});
        break;
      case 'CAPI_META_RESTRICTED_LOCATIONS':
        this.alert.notify('Restricted locations', body.message, 'error', {disableTimeOut: true, tapToDismiss: true});
        break;
      case 'CAPI_META_INVALIDATED_INTERESTS': {
        const interests = body.deprecated_interests?.map((i: any) => i.invalid_name).join(', ') ?? '';
        const message = interests ? `${body.message}: ${interests}` : body.message;
        this.alert.notify('Invalid interests', message, 'error', {disableTimeOut: true, tapToDismiss: true});
        break;
      }
      default:
        this.alert.notify('Error', body?.message || error.message, 'error');
    }
  }

  toNumberFormat(value: number) {
    return ChartService.separator(value);
  }

  boldOr(html: string) {
    return html.replace(/OR/g, "<strong>OR</strong>")
  }

  // Freemium things
  onProjectNameChange(name: string) {
    if (name !== '') {
      // Trigger Event to false to avoid looping through event subscription
      this.creationManager.changeProjectName(name, false);
      if (this.creationManager.projectTitleLink) {
        this.creationManager.audienceTarget.name = name;
        this.creationManager.audienceBenchmark.name = name + ' - Benchmark';
      }
    }
  }
}
